import {
	type BoundActions,
	createHook,
	createStore,
	type HookFunction,
	type StoreActionApi,
} from 'react-sweet-state';

import { type Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { type BrowseCategory } from '../../common/types.ts';

export type AgentDeleteStatus = 'none' | 'success' | 'error';
type AgentDeletion = {
	status: AgentDeleteStatus;
	agent: Agent | null;
};

const browseAgentsUIStore = createStore({
	initialState: {
		searchValue: '',
		selectedTab: 'all-agents' as BrowseCategory,
		selectedAgent: null as Agent | null,
		agentDeletion: {
			status: 'none', // 'none' is effectively the same as 'unconfirmed' and will be used to control the display of a confirmation modal when an `agent` is also set
			agent: null,
		} as AgentDeletion,
	},
	name: 'BrowseAgentsUIStore',
	actions: {
		setSelectedTab:
			(
				selectedTab: BrowseCategory,
			): (({
				setState,
			}: StoreActionApi<{
				searchValue: string;
				selectedTab: BrowseCategory;
				selectedAgent: Agent | null;
				agentDeletion: AgentDeletion;
			}>) => void) =>
			({ setState }) => {
				setState({
					selectedTab,
				});
			},
		setSelectedAgent:
			(
				selectedAgent: Agent | null,
			): (({
				setState,
			}: StoreActionApi<{
				searchValue: string;
				selectedTab: BrowseCategory;
				selectedAgent: Agent | null;
				agentDeletion: AgentDeletion;
			}>) => void) =>
			({ setState }) => {
				setState({
					selectedAgent,
				});
			},
		setSearchValue:
			(
				searchValue: string,
			): (({
				setState,
			}: StoreActionApi<{
				searchValue: string;
				selectedTab: BrowseCategory;
				selectedAgent: Agent | null;
				agentDeletion: AgentDeletion;
			}>) => void) =>
			({ setState }) => {
				setState({
					searchValue,
				});
			},
		setAgentDeletion:
			({
				agent,
				status,
			}: AgentDeletion): (({
				setState,
			}: StoreActionApi<{
				searchValue: string;
				selectedTab: BrowseCategory;
				selectedAgent: Agent | null;
				agentDeletion: AgentDeletion;
			}>) => void) =>
			({ setState }) => {
				setState({
					agentDeletion: {
						agent,
						status,
					},
				});
			},
		resetBrowseAgentsUI:
			(): (({
				setState,
			}: StoreActionApi<{
				searchValue: string;
				selectedTab: BrowseCategory;
				selectedAgent: Agent | null;
				agentDeletion: AgentDeletion;
			}>) => void) =>
			({ setState }) => {
				setState({
					searchValue: '',
					selectedTab: 'all-agents',
					selectedAgent: null,
					agentDeletion: {
						status: 'none',
						agent: null,
					},
				});
			},
	},
});

export const useBrowseAgentsUIStore: HookFunction<
	{
		searchValue: string;
		selectedTab: BrowseCategory;
		selectedAgent: Agent | null;
		agentDeletion: AgentDeletion;
	},
	BoundActions<
		{
			searchValue: string;
			selectedTab: BrowseCategory;
			selectedAgent: Agent | null;
			agentDeletion: AgentDeletion;
		},
		{
			setSelectedTab: (selectedTab: BrowseCategory) => ({
				setState,
			}: StoreActionApi<{
				searchValue: string;
				selectedTab: BrowseCategory;
				selectedAgent: Agent | null;
				agentDeletion: AgentDeletion;
			}>) => void;
			setSelectedAgent: (selectedAgent: Agent | null) => ({
				setState,
			}: StoreActionApi<{
				searchValue: string;
				selectedTab: BrowseCategory;
				selectedAgent: Agent | null;
				agentDeletion: AgentDeletion;
			}>) => void;
			setSearchValue: (searchValue: string) => ({
				setState,
			}: StoreActionApi<{
				searchValue: string;
				selectedTab: BrowseCategory;
				selectedAgent: Agent | null;
				agentDeletion: AgentDeletion;
			}>) => void;
			setAgentDeletion: ({ agent, status }: AgentDeletion) => ({
				setState,
			}: StoreActionApi<{
				searchValue: string;
				selectedTab: BrowseCategory;
				selectedAgent: Agent | null;
				agentDeletion: AgentDeletion;
			}>) => void;
			resetBrowseAgentsUI: () => ({
				setState,
			}: StoreActionApi<{
				searchValue: string;
				selectedTab: BrowseCategory;
				selectedAgent: Agent | null;
				agentDeletion: AgentDeletion;
			}>) => void;
		}
	>,
	void
> = createHook(browseAgentsUIStore);
